import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { _className, EditableDBItemV3, EventType_Create, EventType_Query, LL_H_C, LL_V_L, ModuleFE_Dialog, ModuleFE_MouseInteractivity, ModuleFE_Notifications, mouseInteractivity_ToolTip, NotificationStatus_Failed, openContent, performAction, SimpleListAdapter, TS_Checkbox, TS_Dialog, TS_DropDown, TS_Input, TS_Link, TS_PropRenderer, TS_Tabs, TS_TextArea } from '@nu-art/thunderstorm-frontend/index';
import { arrayIncludesAny, filterDuplicates, filterInstances, MUSTNeverHappenException } from '@nu-art/ts-common';
import { Component_ReferencesTable, Component_TagEditor, Component_VariableSelector, ConfigKeyFE_DiseasesTagId, ConfigKeyFE_Scores, ConfigKeyFE_SystemsTagIds, DropDown_Variable, handleError, HCSDropDowns, ModuleFE_Reference, ModuleFE_Tag, ModuleFE_Variable, Tooltip_VariableInformation, VariableWrapper, WarningDialog } from '@app/hcs-frontend';
import { valueID_YesSuspectedNo } from '@app/hcs-shared';
import { ICONS } from '@app/styles';
import { DropDown_Criticality, getDiseaseProfilePresetData, ModuleFE_ColorGroup, ModuleFE_DiseaseProfile, ModuleFE_MultiVar } from '../../../index.js';
import './Dialog_DiseaseProfileEditor.scss';
import { PermissionsComponent, PermissionsEditableComponent } from '@nu-art/permissions-frontend/index';
import { PermissionKey_DiseaseProfile_Delete, PermissionKey_DiseaseProfile_Edit } from '@app/dp-shared/consts';
import { AccessLevel, ModuleFE_PermissionsAssert } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { clean_DiseaseProfile, CriticalityLabelMap } from '@app/dp-shared';
import { Component_EpidemiologyEditor } from '../../components/Component_EpidemiologyEditor/Component_EpidemiologyEditor.js';
import { Component_PrognosisEditor } from '../../components/Component_PrognosisEditor/Component_PrognosisEditor.js';
export class Dialog_DiseaseProfileEditor extends TS_Dialog {
    // ######################## Static ########################
    static defaultProps = {
        dialogId: 'disease-profile-editor',
    };
    editPermissionKey = ModuleFE_PermissionsAssert.getPermissionKey(PermissionKey_DiseaseProfile_Edit);
    deletePermissionKey = ModuleFE_PermissionsAssert.getPermissionKey(PermissionKey_DiseaseProfile_Delete);
    static async show(disease) {
        ModuleFE_Dialog.show({ content: _jsx(Dialog_DiseaseProfileEditor, { diseaseProfile: disease }), closeOverlayOnClick: () => false });
    }
    // ######################## Life Cycle ########################
    async __onVariableUpdated(...params) {
        const dp = this.state.diseaseProfile.item;
        if (params[0] === EventType_Query) {
            if (params[1][0]._id === dp.variableId) {
                await this.onVariableSelected(params[1][0]);
            }
        }
        else if (params[0] === EventType_Create) {
            if (params[1]._id === dp.variableId) {
                this.reDeriveState();
            }
        }
    }
    shouldComponentUpdate(nextProps, nextState, nextContext) {
        if (this.state.editableTag !== nextState.diseaseProfile.tag)
            return true;
        return super.shouldReDeriveState(nextProps);
    }
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.diseaseProfile ??= this.getEditableDP(nextProps.diseaseProfile ?? clean_DiseaseProfile);
        state.tags ??= VariableWrapper.dbTagsToUI(state.diseaseProfile.item.tagIds || []);
        state.editableTag = state.diseaseProfile.tag;
        return state;
    }
    // ######################## Logic ########################
    getEditableDP(diseaseProfile) {
        return new EditableDBItemV3(diseaseProfile, ModuleFE_DiseaseProfile)
            .setOnChanged(async (editable) => {
            this.setState({ diseaseProfile: editable });
        });
    }
    canBeSaved = () => {
        return !!this.state.diseaseProfile.item.variableId && this.hasSystemTagIds() && !this.state.dialogIsBusy && !this.state.invalidInitialsDPId;
    };
    saveDisease = async () => {
        const dp = this.state.diseaseProfile;
        // @ts-ignore
        delete dp.item.backgroundColor;
        // @ts-ignore
        delete dp.item.color;
        await performAction(async () => {
            this.setState({ dialogIsBusy: true });
            await dp.updateObj({ tagIds: VariableWrapper.getTagsForDB(this.state.tags) });
            await dp.updateObj({ chiefComplaintIds: filterDuplicates(dp.item.chiefComplaintIds || []) });
            try {
                await ModuleFE_DiseaseProfile.v1.upsert(dp.item).executeSync();
            }
            catch (e) {
                handleError(e);
            }
            this.closeDialog();
        }, { type: 'notification', notificationLabels: 'Saving Disease Profile' });
        if (this.mounted)
            this.setState({ dialogIsBusy: false });
    };
    deleteDiseaseProfile = async () => {
        const dp = this.state.diseaseProfile;
        if (!dp.item._id)
            return;
        WarningDialog.showDelete({
            title: `Delete Disease Profile '${dp.item.label}'`,
            executeButtonText: 'Delete',
            warningMessages: [`You are about to delete disease profile '${dp.item.label}'`, 'Are you sure?'],
            onConfirm: async () => {
                await performAction(async () => {
                    this.setState({ dialogIsBusy: true });
                    await ModuleFE_DiseaseProfile._v1.deleteWholeDiseaseProfile({ diseaseProfileId: dp.item._id }).executeSync();
                    this.closeDialog();
                }, {
                    type: 'notification',
                    notificationLabels: 'Deleting Disease Profile'
                });
                if (this.mounted)
                    this.setState({ dialogIsBusy: false });
            }
        });
    };
    onVariableSelected = async (variable) => {
        const dp = this.state.diseaseProfile;
        const initialDPData = getDiseaseProfilePresetData(variable);
        await dp.updateObj({ variableId: variable._id });
        await dp.updateObj({ initials: initialDPData.initials || '' });
        await dp.updateObj({ criticality: initialDPData.criticality });
    };
    hasSystemTagIds() {
        const systemsTagId = ConfigKeyFE_SystemsTagIds.get();
        const variable = ModuleFE_Variable.cache.unique(this.state.diseaseProfile.item.variableId);
        return !!variable?._tagIds.some(tagId => {
            const tag = ModuleFE_Tag.cache.unique(tagId);
            if (!tag)
                throw new MUSTNeverHappenException(`Could not find tag for id ${tagId}`);
            return arrayIncludesAny(tag.tagIds, systemsTagId);
        });
    }
    onInitialsChange = async (value) => {
        const initials = value.toUpperCase();
        await this.state.diseaseProfile.updateObj({ initials: initials });
        const dp = ModuleFE_DiseaseProfile.cache.find(i => i._id !== this.state.diseaseProfile.item._id
            && i.initials === initials
            && initials !== '');
        //Either way, on change close the tooltip
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip);
        if (!dp)
            return this.setState({ invalidInitialsDPId: undefined });
        this.setState({ invalidInitialsDPId: dp._id });
    };
    getInitialsTooltip = () => {
        if (!this.state.invalidInitialsDPId)
            return {};
        const dp = ModuleFE_DiseaseProfile.cache.unique(this.state.invalidInitialsDPId);
        return openContent.tooltip.right('initials-error', () => _jsxs("div", { children: ["\"", dp.initials, "\" is already in use by Disease \u201C", dp.label, "\". Please select different profile initials."] }));
    };
    hideField = (field) => {
        const dp = this.state.diseaseProfile.item;
        //Hide field if it has no data AND (either dp is completed OR user doesn't have edit permissions)
        return !dp[field]
            && (dp.completed
                || ModuleFE_PermissionsAssert.getAccessLevel(this.editPermissionKey) !== AccessLevel.HasAccess);
    };
    isInEditMode = () => {
        const dp = this.state.diseaseProfile;
        if (dp.item.completed)
            return false;
        return ModuleFE_PermissionsAssert.getAccessLevel(this.editPermissionKey) === AccessLevel.HasAccess;
    };
    // ######################## Render ########################
    buttons = () => {
        const buttons = { left: [], right: [] };
        buttons.right.push({ content: 'Cancel', onClick: () => this.closeDialog(), disabled: this.state.dialogIsBusy });
        if (ModuleFE_PermissionsAssert.getAccessLevel(this.editPermissionKey) === AccessLevel.HasAccess)
            buttons.right.push({
                content: 'Save',
                className: _className(this.canBeSaved() ? 'desired-action-button' : undefined),
                onClick: this.saveDisease,
                disabled: !this.canBeSaved(),
            });
        if (this.state.diseaseProfile.item._id && ModuleFE_PermissionsAssert.getAccessLevel(this.deletePermissionKey) === AccessLevel.HasAccess)
            buttons.left.push({
                content: 'Delete',
                className: 'delete-button',
                onClick: this.deleteDiseaseProfile,
                disabled: this.state.dialogIsBusy,
            });
        return buttons;
    };
    renderHeader = () => {
        const dp = this.state.diseaseProfile;
        return _jsxs(_Fragment, { children: [_jsx("span", { className: "header__title", children: (dp.item._id ? 'Edit Disease Profile' : 'Add Disease Profile') + (dp.item.label ? (' - ' + dp.item.label) : '') }), _jsxs(LL_H_C, { className: 'dialog__header__buttons', children: [this.renderCompleted(), _jsx(ICONS.x.component, { className: "exit_icon", onClick: () => this.closeDialog() })] })] });
    };
    renderBody = () => {
        const editMode = this.isInEditMode();
        const tabs = [
            { title: 'General', content: this.renderGeneralTab, uid: 'general' },
            {
                title: 'Epidemiology',
                content: () => _jsx(Component_EpidemiologyEditor, { editable: this.state.diseaseProfile, editMode: editMode }),
                uid: 'epidemiology'
            },
            {
                title: 'Prognosis',
                content: () => _jsx(Component_PrognosisEditor, { editable: this.state.diseaseProfile, editMode: editMode }),
                uid: 'prognosis'
            },
            { title: 'References', content: this.renderReferencesTab, uid: 'references' },
        ];
        return _jsx(TS_Tabs, { tabs: tabs, tabsContentClass: 'custom-scrollbar__vertical' });
    };
    renderCompleted = () => {
        const dp = this.state.diseaseProfile;
        return _jsx(PermissionsComponent, { permissionKey: this.editPermissionKey, children: _jsxs(LL_H_C, { className: 'match_width dp-complete', children: [_jsx("span", { className: 'dialog__field-title', children: "Completed Disease Profile:" }), _jsx(TS_Checkbox, { className: 'quai', icon: _jsx(ICONS.v.component, {}), checked: dp.item.completed, onCheck: async () => {
                            await dp.updateObj({ completed: !dp.item.completed });
                            this.forceUpdate();
                        } })] }) });
    };
    // ######################## Render - General #################
    renderGeneralTab = () => {
        return _jsxs(LL_V_L, { className: 'general-tab', children: [_jsxs(LL_H_C, { className: 'columns-container', children: [_jsxs(LL_V_L, { className: 'col', children: [this.renderDiseaseDropdown(), this.renderLabel(), this.renderInitials(), this.renderICD(), this.renderCriticality(), this.renderVariantGroup()] }), _jsxs(LL_V_L, { className: 'col', children: [this.renderTagEditor(), this.renderScores()] })] }), this.renderDescription(), this.renderNotes()] });
    };
    renderDiseaseDropdown() {
        const dp = this.state.diseaseProfile;
        const diseaseTagId = ConfigKeyFE_DiseasesTagId.get();
        const hasSystemTagIds = this.hasSystemTagIds();
        const variable = ModuleFE_Variable.cache.unique(dp.item.variableId);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Disease Variable', children: _jsxs(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, value: variable?.name, forceLock: dp.item.completed, children: [_jsx(Component_VariableSelector, { onVariableSelected: this.onVariableSelected, selectedVariableId: dp.item.variableId, queryFilter: item => item._tagIds.includes(diseaseTagId) && item.valueId === valueID_YesSuspectedNo, placeholder: 'Select Profile Variable', allowSelection: !dp.item._id, tailRenderer: () => {
                            if (!dp.item.variableId)
                                return '';
                            return _jsx("div", { className: 'variable-info-tooltip-icon', ...openContent.tooltip.right('variable-info-tooltip', () => _jsx(Tooltip_VariableInformation, { variable: ModuleFE_Variable.cache.unique(dp.item.variableId), showDirection: 'right' }), { contentHoverDelay: 200 }), children: "i" });
                        } }), (!!dp.item.variableId && !hasSystemTagIds) &&
                        _jsx("div", { className: 'missing-tag', children: "This Disease Profile's variable must have an etiology or system tag" })] }) });
    }
    renderLabel = () => {
        const dp = this.state.diseaseProfile;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(PermissionsEditableComponent, { value: dp.item.label, forceLock: dp.item.completed, permissionKey: this.editPermissionKey, children: _jsx(TS_Input, { type: 'text', value: dp.item.label || '', onChange: async (value) => {
                        await dp.updateObj({ label: value });
                        this.forceUpdate();
                    } }) }) });
    };
    renderVariantGroup = () => {
        const dp = this.state.diseaseProfile;
        const selectedVariant = ModuleFE_Tag.cache.unique(dp.item.variantsGroupId);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Variant Group', children: _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: selectedVariant?.label, children: _jsx(HCSDropDowns.Tag, { queryFilter: (tag) => tag.tagIds.length === 0, selected: dp.item.variantsGroupId, onSelected: async (tagGroup) => dp.updateObj({ variantsGroupId: tagGroup._id }) }) }) });
    };
    renderInitials = () => {
        const dp = this.state.diseaseProfile;
        const adapter = SimpleListAdapter(ModuleFE_ColorGroup.cache.allMutable(), (item) => _jsx("div", { children: item.item.label }));
        const selected = ModuleFE_ColorGroup.cache.unique(dp.item.colorGroup);
        return _jsxs(LL_H_C, { className: 'disease-editor__container__disease-initials', children: [_jsx(TS_PropRenderer.Vertical, { label: 'Profile Initials', children: _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: dp.item.initials, children: _jsx(TS_Input, { className: this.state.invalidInitialsDPId ? 'error' : undefined, id: `disease-editor__disease-initials`, placeholder: 'Disease initials', type: "text", maxLength: 4, value: dp.item.initials, onChange: this.onInitialsChange, ...this.getInitialsTooltip() }) }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Color Group', className: 'disease-editor__container__disease-initials__previews', children: _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: this.state.diseaseProfile.item.completed, value: selected?.label ?? '-', children: _jsx(TS_DropDown, { className: 'match_width', placeholder: 'Select Related System', selected: selected, adapter: adapter, onSelected: (group) => dp.set('colorGroup', group._id) }) }) })] });
    };
    renderICD = () => {
        const icds = this.state.diseaseProfile.item.icd;
        if (!icds?.length)
            return '';
        return _jsx(TS_PropRenderer.Vertical, { label: 'ICD', children: _jsx("span", { children: icds.map(refId => ModuleFE_Reference.cache.unique(refId).data.id).join(', ') }) });
    };
    renderTagEditor = () => {
        const disableEdit = this.state.diseaseProfile.item.completed || ModuleFE_PermissionsAssert.getAccessLevel(this.editPermissionKey) !== AccessLevel.HasAccess;
        if (!this.state.tags?.length && disableEdit)
            return;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Tags', children: _jsx(Component_TagEditor, { tags: this.state.tags, singleTagFilter: (tag, groupTag) => tag.tagIds[0] === groupTag._id, groupTagFilter: (groupTag) => true, disableEdit: disableEdit }) });
    };
    renderCriticality = () => {
        const dp = this.state.diseaseProfile;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Criticality', children: _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: dp.item.criticality ? CriticalityLabelMap[dp.item.criticality] : '-', children: _jsx(DropDown_Criticality.editable, { editable: dp, prop: 'criticality' }) }) });
    };
    renderScores = () => {
        const dp = this.state.diseaseProfile;
        const existingScoreVarIds = filterInstances(dp.item.scoreReferences?.map(i => i.variableId) || []);
        const scoresTag = ConfigKeyFE_Scores.get();
        return _jsxs(TS_PropRenderer.Vertical, { label: 'Scores', children: [_jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: dp.item.scores, className: 'text-area', children: _jsx(TS_TextArea, { type: 'text', placeholder: 'Scores goes here...', value: dp.item.scores, onChange: async (value) => {
                            await dp.updateObj({ scores: value });
                            this.forceUpdate();
                        } }) }), _jsxs(LL_V_L, { className: 'match_width', children: [dp.item.scoreReferences?.map(score => {
                            const scoreVar = ModuleFE_Variable.cache.unique(score.variableId);
                            return _jsxs(LL_H_C, { className: 'score-reference', children: [_jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: scoreVar?.name, className: 'grid-data', children: _jsx(DropDown_Variable, { canUnselect: true, selected: scoreVar, onSelected: async (variable) => {
                                                if (!variable)
                                                    dp.item.scoreReferences = dp.item.scoreReferences?.filter(i => i.variableId !== score.variableId);
                                                else
                                                    score.variableId = variable._id;
                                                this.forceUpdate();
                                            }, queryFilter: i => !existingScoreVarIds.includes(i._id) && i.tagIds.some(group => group.value.includes(scoresTag)), sortBy: ['name'], placeholder: 'Score Name' }) }), this.renderScoreLink(score)] });
                        }), _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: '', children: _jsx(DropDown_Variable, { selected: undefined, onSelected: async (variable) => {
                                    if (!dp.item.scoreReferences)
                                        dp.item.scoreReferences = [];
                                    dp.item.scoreReferences.push({
                                        variableId: variable._id,
                                    });
                                    this.forceUpdate();
                                }, queryFilter: i => !existingScoreVarIds.includes(i._id) && i.tagIds.some(group => group.value.includes(scoresTag)), sortBy: ['name'], placeholder: 'Score Name' }) })] })] });
    };
    renderScoreLink = (score) => {
        const editMode = !this.state.diseaseProfile.item.completed;
        if (!editMode) {
            if (!score.mdCalcUrl)
                return _jsx("div", { className: 'grid-data' });
            return _jsx(TS_Link, { url: score.mdCalcUrl, target: '_blank', className: 'grid-data', children: score.mdCalcUrl });
        }
        return _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: !editMode, value: score.mdCalcUrl, children: _jsx(TS_Input, { type: 'text', value: score.mdCalcUrl, onChange: val => score.mdCalcUrl = val }) });
    };
    renderDescription = () => {
        if (this.hideField('profileDescription'))
            return '';
        const dp = this.state.diseaseProfile;
        return _jsx(TS_PropRenderer.Vertical, { label: 'General Description', children: _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: dp.item.profileDescription, className: 'text-area', children: _jsx(TS_TextArea, { type: 'text', placeholder: 'Disease description goes here...', value: dp.item.profileDescription, onChange: async (value) => {
                        await dp.updateObj({ profileDescription: value });
                        this.forceUpdate();
                    } }) }) });
    };
    renderNotes = () => {
        if (this.hideField('profileNotes'))
            return '';
        const dp = this.state.diseaseProfile;
        return _jsx(TS_PropRenderer.Vertical, { label: 'General Notes', children: _jsx(PermissionsEditableComponent, { permissionKey: this.editPermissionKey, forceLock: dp.item.completed, value: dp.item.profileNotes, className: 'text-area', children: _jsx(TS_TextArea, { type: 'text', placeholder: 'Enter notes here...', value: dp.item.profileNotes, onChange: async (value) => {
                        await dp.updateObj({ profileNotes: value });
                        this.forceUpdate();
                    } }) }) });
    };
    // ######################## Render - References #################
    renderReferencesTab = () => {
        return _jsx(LL_V_L, { className: 'references-tab', children: this.renderRefsTable() });
    };
    renderRefsTable = () => {
        const dp = this.state.diseaseProfile;
        const onReferenceDeleted = (referenceId) => {
            const mvs = ModuleFE_MultiVar.cache.filter(i => i.diseaseProfileId === dp.item._id
                && i.referenceIds.includes(referenceId));
            if (mvs.length) {
                ModuleFE_Notifications.create()
                    .content('MultiVars are using this reference', mvs.map(i => ModuleFE_Variable.cache.unique(i._firstVarId).name).join(', '))
                    .setStatus(NotificationStatus_Failed).show(5000);
                return;
            }
            const index = dp.item.referenceIds.findIndex(refId => refId === referenceId);
            if (index === -1)
                return;
            dp.item.referenceIds.splice(index, 1);
            this.forceUpdate();
        };
        const onReferenceUpsert = async (reference) => {
            if (dp.item.referenceIds.findIndex(refId => refId === reference._id) === -1)
                dp.item.referenceIds.push(reference._id);
            this.forceUpdate();
        };
        const allowedRef = ['book', 'article', 'upToDate'];
        return _jsx(TS_PropRenderer.Vertical, { label: 'Profile References', children: _jsx("div", { className: 'list-wrapper', children: _jsx("div", { className: 'custom-scrollbar__vertical', onContextMenu: e => {
                        Component_ReferencesTable.handleReferencesRightClick(e, onReferenceUpsert, onReferenceDeleted, allowedRef);
                    }, children: dp && _jsx(Component_ReferencesTable, { referenceIds: dp.item.referenceIds, onReferenceUpsertCallback: onReferenceUpsert, onReferenceDeletedCallback: onReferenceDeleted, allowedRefs: allowedRef }) }) }) });
    };
}
//# sourceMappingURL=Dialog_DiseaseProfileEditor.js.map